import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { ChatSession } from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { FolderMinus, MoreHorizontal, Pencil, Star, StarOff, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ChatSessionMenuProps {
  session: ChatSession;
  onStartRename: () => void;
}

export function ChatSessionMenu({ session, onStartRename }: ChatSessionMenuProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const favoriteMutation = useMutation({
    mutationFn: () => api.chatToggleFavorite(session.id, { isFavorite: !session.isFavorite }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat', 'sessions'] }),
  });

  const unfileMutation = useMutation({
    mutationFn: () => api.chatMoveSession(session.id, { projectId: null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'sessions'] });
      qc.invalidateQueries({ queryKey: ['chat', 'projects'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.chatDeleteSession(session.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'sessions'] });
      if (location.pathname === `/chat/${session.id}`) {
        navigate({ to: '/chat' });
      }
    },
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', handler);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 4,
        left: rect.right + 4,
      });
    }
    setOpen((v) => !v);
  };

  const close = () => setOpen(false);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="채팅 메뉴"
        onClick={toggle}
        className="size-7 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-strong hover:text-foreground"
      >
        <MoreHorizontal className="size-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: pos.top, left: pos.left }}
              className="w-44 z-50 rounded-md border border-border bg-surface shadow-lg py-1 text-sm"
            >
              <MenuItem
                icon={session.isFavorite ? StarOff : Star}
                onClick={() => {
                  close();
                  favoriteMutation.mutate();
                }}
              >
                {session.isFavorite ? '즐겨찾기 해제' : '즐겨찾기'}
              </MenuItem>
              <MenuItem
                icon={Pencil}
                onClick={() => {
                  close();
                  onStartRename();
                }}
              >
                이름 변경
              </MenuItem>
              {session.projectId ? (
                <MenuItem
                  icon={FolderMinus}
                  onClick={() => {
                    close();
                    unfileMutation.mutate();
                  }}
                >
                  프로젝트에서 빼기
                </MenuItem>
              ) : null}
              <div className="my-1 border-t border-border" />
              <MenuItem
                icon={Trash2}
                danger
                onClick={() => {
                  close();
                  if (window.confirm('이 대화를 삭제할까요?')) {
                    deleteMutation.mutate();
                  }
                }}
              >
                삭제
              </MenuItem>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function MenuItem({
  children,
  onClick,
  icon: Icon,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm hover:bg-surface-muted',
        danger ? 'text-destructive' : 'text-foreground',
      )}
    >
      <Icon className="size-4 shrink-0" />
      <span>{children}</span>
    </button>
  );
}
